import { ExpertiseEnum } from "./expertiseEnum";
import {
  expertiseSpecialLinks,
  ExpertiseSpecialLinks,
} from "./expertiseSpecialLinks";
import { expertiseSlugs } from "./slRoutes";
import {
  getExpertiseStrings,
  ExpertiseStringSet,
} from "./strings/expertiseStrings";
import type { Locale } from "./i18n/locales";

export interface Expertise extends ExpertiseStringSet {
  slug: string;
  specialLinks?: ExpertiseSpecialLinks;
}

/**
 * Expertise areas that should be hidden from navigation and public display.
 * The data is kept but not shown to users.
 */
export const hiddenExpertiseAreas: ExpertiseEnum[] = [
  ExpertiseEnum.InternetMedienRecht,
  ExpertiseEnum.Prozessfuehrung,
];

function buildExpertiseData(
  strings: Record<ExpertiseEnum, ExpertiseStringSet>,
): Record<ExpertiseEnum, Expertise> {
  return Object.fromEntries(
    Object.entries(strings).map(([key, expertise]) => {
      const expertiseType = key as ExpertiseEnum;
      const specialLinks = expertiseSpecialLinks[expertiseType];

      return [
        key,
        {
          ...expertise,
          slug: expertiseSlugs[expertiseType],
          ...(specialLinks && { specialLinks }),
        },
      ];
    }),
  ) as Record<ExpertiseEnum, Expertise>;
}

export function getExpertiseData(
  locale: Locale,
): Record<ExpertiseEnum, Expertise> {
  return buildExpertiseData(getExpertiseStrings(locale));
}

/** German expertise catalog (default for static params and legacy imports). */
export const expertiseData: Record<ExpertiseEnum, Expertise> =
  getExpertiseData("de");

export function getVisibleExpertiseData(
  locale: Locale,
): Partial<Record<ExpertiseEnum, Expertise>> {
  const data = getExpertiseData(locale);
  return Object.fromEntries(
    Object.entries(data).filter(
      ([key]) => !hiddenExpertiseAreas.includes(key as ExpertiseEnum),
    ),
  ) as Partial<Record<ExpertiseEnum, Expertise>>;
}

export const visibleExpertiseData: Partial<Record<ExpertiseEnum, Expertise>> =
  getVisibleExpertiseData("de");

export function getExpertiseBySlug(
  slug: string,
  locale: Locale,
): Expertise | undefined {
  return Object.values(getExpertiseData(locale)).find(
    (expertise) => expertise.slug === slug,
  );
}
